import React from "react";
import TypewriterText from "../../components/TypewriterText";
import ParallaxLayer from "../../components/ParallaxLayer";
import type { CardProps, HeroProps, SectionHeaderProps } from "../types";
import "./shining.css";

/**
 * THE SHINING — the Overlook after dark.
 *
 * Three things carry it: a frame of the elevator sunk into the dark, the typed
 * page — which is text, so it costs nothing — and Kubrick's dead-centre
 * symmetry, which is a layout rule rather than an image.
 */

/*
 * A 17th-century English proverb, not the film's invention — which is why the
 * page can carry it. In the film it fills hundreds of sheets, so a wall of it
 * is faithful rather than a reference bolted on.
 */
const LINE = "All work and no play makes Jack a dull boy.";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const ShiningHero: React.FC<HeroProps> = ({ name, tagline }) => (
  <>
    <p className="sh-eyebrow">The Overlook Hotel</p>
    <h1 className="hero-title sh-hero-title">
      <TypewriterText text={name.toUpperCase()} caret />
    </h1>
    <p className="hero-subtitle sh-tagline">
      <TypewriterText text={tagline} />
    </p>
  </>
);

/** Sections are rooms. Numbered, centred, announced. */
export const ShiningSectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  index,
}) => (
  <h2 className="section-heading sh-section-heading">
    <span className="sh-room">Room {237 + index * 100}</span>
    <span className="sh-section-title">
      <TypewriterText text={title.toUpperCase()} caret />
    </span>
    <span className="sh-rule" aria-hidden="true" />
  </h2>
);

/** Cards sit dead centre, the way every shot in this film is composed. */
export const ShiningCard: React.FC<CardProps> = ({ item }) => (
  <div className="item sh-card">
    <div className="sh-card-head">
      <h3 className="item-title sh-card-title">
        {item.href ? (
          <a
            className="item-title-link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <TypewriterText text={item.title} />
          </a>
        ) : (
          <TypewriterText text={item.title} />
        )}
      </h3>
      {item.meta && <span className="sh-card-meta">{item.meta}</span>}
    </div>

    {item.subtitle && (
      <p className="sh-card-role">
        <TypewriterText text={item.subtitle} />
      </p>
    )}

    <p className="item-description sh-card-description">
      <TypewriterText text={item.description} />
    </p>

    {item.action && (
      <a
        href={item.action.href}
        target="_blank"
        rel="noopener noreferrer"
        className="item-link sh-card-action"
      >
        {item.action.label}
      </a>
    )}
  </div>
);

/**
 * The manuscript.
 *
 * A wall of the same typed line, with one more sheet arriving every few
 * seconds — someone upstairs is still at the typewriter. Kept faint enough to
 * read as texture rather than as something demanding to be read.
 */
export const ShiningBackdrop: React.FC = () => {
  const [lines, setLines] = React.useState(30);

  // Fast and relentless — someone upstairs is hammering the keys, not
  // thoughtfully composing. Resets once the page is full and starts again.
  React.useEffect(() => {
    if (prefersReducedMotion()) return;
    const timer = window.setInterval(
      () => setLines((n) => (n >= 54 ? 30 : n + 1)),
      380
    );
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="sh-backdrop" aria-hidden="true">
      <ParallaxLayer
        className="sh-elevator"
        factor={0.035}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/refs/shining-elevator.jpg)`,
        }}
      />
      <div className="sh-elevator-scrim" />
      <ParallaxLayer className="sh-manuscript" factor={0.09}>
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className="sh-manuscript-line">
            {LINE}
          </span>
        ))}
      </ParallaxLayer>
    </div>
  );
};

/** Hotel light and the corridor closing in at the edges. */
export const ShiningChrome: React.FC = () => (
  <>
    <div className="sh-lamp" aria-hidden="true" />
    <div className="sh-vignette" aria-hidden="true" />
  </>
);
